import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Workflow, Play, Plus, Pencil, Trash2, Loader2, RefreshCw, AlertTriangle, FileWarning, Terminal, CheckCircle2, XCircle, Clock, CalendarClock } from 'lucide-react';

const STATUS_META = {
  pending: { cls: 'bg-amber-500/15 text-amber-600', Icon: Clock },
  running: { cls: 'bg-blue-500/15 text-blue-600', Icon: Loader2 },
  succeeded: { cls: 'bg-emerald-500/15 text-emerald-600', Icon: CheckCircle2 },
  failed: { cls: 'bg-red-500/15 text-red-600', Icon: XCircle },
};

const StatusBadge = ({ status, testId }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <Badge className={m.cls} variant="secondary" data-testid={testId}>
      <m.Icon className={`w-3 h-3 mr-1 ${status === 'running' ? 'animate-spin' : ''}`} />{status}
    </Badge>
  );
};

export default function DataPipelines() {
  const { api } = useAuth();
  const [pipelines, setPipelines] = useState([]);
  const [selected, setSelected] = useState(null);
  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null);
  const [runOpen, setRunOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', code: '', schedule_enabled: false, schedule_time: '' });
  const [saving, setSaving] = useState(false);
  const [failures, setFailures] = useState(null);
  const pollRef = useRef(null);
  const logsEndRef = useRef(null);

  const loadPipelines = useCallback(async () => {
    try {
      const res = await api().get('/pipelines');
      setPipelines(res.data.pipelines || []);
    } catch (e) { toast.error('Failed to load pipelines'); }
  }, [api]);

  const loadRuns = useCallback(async (pid) => {
    try {
      const res = await api().get(`/pipelines/${pid}/runs`);
      setRuns(res.data.runs || []);
    } catch (e) { /* noop */ }
  }, [api]);

  const loadFailures = useCallback(async () => {
    try {
      const res = await api().get('/pipelines/report/failures');
      setFailures(res.data);
    } catch (e) { /* noop */ }
  }, [api]);

  useEffect(() => { loadPipelines(); loadFailures(); }, [loadPipelines, loadFailures]);
  useEffect(() => { if (selected) loadRuns(selected.id); }, [selected, loadRuns]);

  const fetchRun = useCallback(async (rid) => {
    try {
      const res = await api().get(`/pipelines/runs/${rid}`);
      setRun(res.data);
      return res.data;
    } catch (e) { return null; }
  }, [api]);

  useEffect(() => {
    if (!run || !['pending', 'running'].includes(run.status)) {
      clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      const r = await fetchRun(run.id);
      if (r && !['pending', 'running'].includes(r.status)) {
        clearInterval(pollRef.current);
        if (selected) loadRuns(selected.id);
        loadPipelines();
        loadFailures();
        toast[r.status === 'succeeded' ? 'success' : 'error'](
          r.status === 'succeeded' ? 'Pipeline run succeeded' : `Run failed: ${r.error || 'unknown'}`);
      }
    }, 1500);
    return () => clearInterval(pollRef.current);
  }, [run?.id, run?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { logsEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [run?.logs?.length]);

  const openCreate = async () => {
    setEditing(null);
    let code = '';
    try {
      const res = await api().get('/pipelines/template');
      code = res.data.code;
    } catch (e) { /* noop */ }
    setForm({ name: '', description: '', code, schedule_enabled: false, schedule_time: '' });
    setEditOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description || '', code: p.code,
      schedule_enabled: !!p.schedule_enabled, schedule_time: p.schedule_time || '' });
    setEditOpen(true);
  };

  const savePipeline = async () => {
    if (!form.name.trim() || !form.code.trim()) { toast.error('Name and code are required'); return; }
    if (form.schedule_enabled && !form.schedule_time) { toast.error('Pick a schedule time (UTC)'); return; }
    setSaving(true);
    try {
      const payload = { ...form, schedule_time: form.schedule_time || null };
      if (editing) {
        const res = await api().put(`/pipelines/${editing.id}`, payload);
        if (selected?.id === editing.id) setSelected(res.data);
        toast.success('Pipeline updated');
      } else {
        await api().post('/pipelines', payload);
        toast.success('Pipeline created');
      }
      setEditOpen(false);
      loadPipelines();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed — check your code for unsafe constructs');
    } finally {
      setSaving(false);
    }
  };

  const deletePipeline = async (p) => {
    await api().delete(`/pipelines/${p.id}`);
    if (selected?.id === p.id) { setSelected(null); setRuns([]); }
    loadPipelines();
    toast.success('Pipeline deleted');
  };

  const runPipeline = async (p) => {
    try {
      const res = await api().post(`/pipelines/${p.id}/run`);
      setSelected(p);
      setRunOpen(true);
      await fetchRun(res.data.run_id);
      loadRuns(p.id);
      toast.info('Pipeline started');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start run');
    }
  };

  const openRun = async (rid) => {
    setRunOpen(true);
    await fetchRun(rid);
  };

  return (
    <div className="space-y-6" data-testid="pipelines-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Manrope'] flex items-center gap-3">
            <Workflow className="w-7 h-7 text-primary" /> Data Pipelines
          </h1>
          <p className="text-muted-foreground mt-1">Write and run sandboxed Python pipelines against Azure SQL with AI helpers.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { loadPipelines(); loadFailures(); if (selected) loadRuns(selected.id); }} data-testid="pipelines-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={openCreate} data-testid="pipelines-create-btn">
            <Plus className="w-4 h-4 mr-2" /> New Pipeline
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Pipelines</CardTitle>
            <CardDescription>Helpers available in scripts: sql_query, sql_execute, ai, log, re, json. Set <code>output</code> for results.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="pipelines-list">
            {pipelines.length === 0 && <p className="text-sm text-muted-foreground">No pipelines yet — create one to get started.</p>}
            {pipelines.map((p) => (
              <div key={p.id}
                className={`p-3 rounded-lg border flex items-center justify-between gap-3 cursor-pointer transition-colors hover:bg-muted/60 ${selected?.id === p.id ? 'border-primary bg-primary/5' : ''}`}
                onClick={() => setSelected(p)} data-testid={`pipeline-item-${p.id}`}>
                <div className="min-w-0">
                  <p className="font-medium truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.description || 'No description'}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {p.schedule_enabled && p.schedule_time && (
                    <Badge variant="secondary" className="bg-cyan-500/15 text-cyan-700" data-testid={`pipeline-schedule-badge-${p.id}`}>
                      <CalendarClock className="w-3 h-3 mr-1" /> daily {p.schedule_time} UTC
                    </Badge>
                  )}
                  {p.last_run_status && <StatusBadge status={p.last_run_status} testId={`pipeline-last-status-${p.id}`} />}
                  <Button size="sm" onClick={(e) => { e.stopPropagation(); runPipeline(p); }} data-testid={`pipeline-run-btn-${p.id}`}>
                    <Play className="w-4 h-4 mr-1" /> Run
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); openEdit(p); }} data-testid={`pipeline-edit-btn-${p.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); deletePipeline(p); }} data-testid={`pipeline-delete-btn-${p.id}`}>
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              </div>
            ))}

            {selected && (
              <div className="mt-4 pt-4 border-t space-y-2" data-testid="pipeline-runs-section">
                <p className="text-sm font-semibold">Run history — {selected.name}</p>
                {runs.length === 0 && <p className="text-sm text-muted-foreground">No runs yet.</p>}
                {runs.map((r) => (
                  <div key={r.id} className="flex items-center justify-between p-2 rounded-md border cursor-pointer hover:bg-muted/60"
                    onClick={() => openRun(r.id)} data-testid={`pipeline-run-item-${r.id}`}>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={r.status} />
                      <span className="text-sm text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
                      {r.triggered_by === 'schedule' && (
                        <Badge variant="secondary" className="bg-cyan-500/15 text-cyan-700 text-xs">
                          <CalendarClock className="w-3 h-3 mr-1" /> scheduled
                        </Badge>
                      )}
                    </div>
                    {r.error && <span className="text-xs text-red-500 truncate max-w-[220px]">{r.error}</span>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileWarning className="w-5 h-5 text-red-500" /> Failure Report
              {failures && <Badge variant="secondary" data-testid="failures-total-badge">{failures.total_failed}</Badge>}
            </CardTitle>
            <CardDescription>Recent failed pipeline, matching and massaging jobs.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[560px] overflow-y-auto" data-testid="failures-report">
            {failures && failures.total_failed === 0 && (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" /> No failed jobs.
              </p>
            )}
            {failures?.pipeline_failures?.map((f) => (
              <div key={f.id} className="p-2 rounded-md border border-red-200 cursor-pointer hover:bg-muted/60"
                onClick={() => openRun(f.id)} data-testid={`failure-pipeline-${f.id}`}>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-purple-500/15 text-purple-600">pipeline</Badge>
                  <p className="text-sm font-medium truncate">{f.pipeline_name}</p>
                </div>
                <p className="text-xs text-red-600 mt-1 line-clamp-2">{f.error}</p>
                <p className="text-xs text-muted-foreground">{f.created_at ? new Date(f.created_at).toLocaleString() : ''}</p>
              </div>
            ))}
            {failures?.matching_failures?.map((f) => (
              <div key={f.id} className="p-2 rounded-md border border-red-200" data-testid={`failure-matching-${f.id}`}>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-blue-500/15 text-blue-600">matching</Badge>
                  <p className="text-sm font-medium truncate">{f.name}</p>
                </div>
                <p className="text-xs text-red-600 mt-1 line-clamp-2">{f.error}</p>
              </div>
            ))}
            {failures?.massaging_failures?.map((f) => (
              <div key={f.id} className="p-2 rounded-md border border-red-200" data-testid={`failure-massaging-${f.id}`}>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-pink-500/15 text-pink-600">massaging</Badge>
                  <p className="text-sm font-medium truncate">{f.name}</p>
                </div>
                <p className="text-xs text-red-600 mt-1 line-clamp-2">{f.error}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Pipeline' : 'New Pipeline'}</DialogTitle>
            <DialogDescription>
              Sandboxed Python — no imports or file access. Use sql_query / sql_execute / ai / log; set <code>output</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Nightly legislation sync" data-testid="pipeline-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What does this pipeline do?" data-testid="pipeline-description-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Python code</Label>
              <Textarea value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                rows={16} className="font-mono text-xs" spellCheck={false} data-testid="pipeline-code-input" />
            </div>
            <div className="flex flex-wrap items-center gap-4 p-3 rounded-lg border bg-muted/40" data-testid="pipeline-schedule-section">
              <div className="flex items-center gap-2">
                <Switch checked={form.schedule_enabled}
                  onCheckedChange={(v) => setForm({ ...form, schedule_enabled: v })}
                  data-testid="pipeline-schedule-switch" />
                <Label className="flex items-center gap-1"><CalendarClock className="w-4 h-4" /> Run automatically every day</Label>
              </div>
              {form.schedule_enabled && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-muted-foreground">at</Label>
                  <Input type="time" value={form.schedule_time}
                    onChange={(e) => setForm({ ...form, schedule_time: e.target.value })}
                    className="w-32" data-testid="pipeline-schedule-time-input" />
                  <span className="text-xs text-muted-foreground">UTC</span>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="pipeline-cancel-btn">Cancel</Button>
              <Button onClick={savePipeline} disabled={saving} data-testid="pipeline-save-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editing ? 'Save Changes' : 'Create Pipeline'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Run detail dialog */}
      <Dialog open={runOpen} onOpenChange={(o) => { setRunOpen(o); if (!o) setRun(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Terminal className="w-5 h-5" /> Run {run?.id?.slice(0, 8)}
              {run && <StatusBadge status={run.status} testId="run-detail-status" />}
            </DialogTitle>
            <DialogDescription>{run?.pipeline_name}</DialogDescription>
          </DialogHeader>
          {run && (
            <div className="space-y-4">
              <div className="rounded-lg bg-zinc-950 text-zinc-100 p-3 font-mono text-xs max-h-56 overflow-y-auto" data-testid="run-logs-console">
                {(run.logs || []).map((l, i) => (
                  <div key={i} className={l.level === 'error' ? 'text-red-400' : ''}>
                    <span className="text-zinc-500">{l.ts?.slice(11, 19)}</span> {l.message}
                  </div>
                ))}
                {(run.logs || []).length === 0 && <span className="text-zinc-500">Waiting for logs...</span>}
                <div ref={logsEndRef} />
              </div>
              {run.error && (
                <div className="p-3 rounded-md bg-red-500/10 text-red-700 text-sm flex items-start gap-2" data-testid="run-error-box">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {run.error}
                </div>
              )}
              {run.result !== null && run.result !== undefined && (
                <div className="space-y-1">
                  <p className="text-sm font-semibold">Output</p>
                  {Array.isArray(run.result) && run.result.length > 0 && typeof run.result[0] === 'object' ? (
                    <div className="border rounded-lg max-h-72 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-muted sticky top-0">
                          <tr>{Object.keys(run.result[0]).map((k) => <th key={k} className="text-left p-2 font-medium">{k}</th>)}</tr>
                        </thead>
                        <tbody data-testid="run-result-table">
                          {run.result.slice(0, 200).map((row, i) => (
                            <tr key={i} className="border-t">
                              {Object.keys(run.result[0]).map((k) => (
                                <td key={k} className="p-2 max-w-[200px] truncate" title={String(row[k] ?? '')}>{String(row[k] ?? '')}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <pre className="p-3 rounded-lg bg-muted text-xs max-h-72 overflow-auto" data-testid="run-result-json">
                      {typeof run.result === 'string' ? run.result : JSON.stringify(run.result, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
